import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import NucleusPage, { Verb } from '../../components/nucleus/NucleusPage';
import DataTable from '../../components/nucleus/DataTable';
import { Empty, ErrorEmpty, ShowMore } from '../../components/nucleus/Empty';
import { Row } from '../../components/nucleus/Collection';
import { TitledSet } from '../../components/nucleus/SetHead';
import { useFacets, Faceted } from '../../components/nucleus/FacetRail';
import Inspector, { Band, Facts, Note, Tags, useOpen } from '../../components/nucleus/Inspector';
import { useToast } from '../../components/common';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import { ago, whenOf } from '../../nucleus/format';
import { PluginFace, RunDot } from './cells';
import { ReadSheet } from './sheets';
import { blank } from './fields';
import { autosApi, pluginsApi } from './data/api';
import { refusalOf } from './data/items';
import { useRunFeed, useRun, usePluginRequests, usePluginList } from './data/hooks';
import { useLinkedRun } from './data/linked';
import {
    OUTCOMES, STARTED_BY_KINDS, outcomeWord, outcomeTone, dotTone, leftOutLine,
} from './data/words';
import {
    runCounts, happenedLine, tookWord, expiresWord, toolKey, toolOfKey, executionOutcome, startRealm,
} from './data/toolruns';
import RequestSheet from './RequestSheet';
import './runs.css';

// RUNS (plugin lifecycle §3.1, §3.8) — every run of a plugin's tool in this
// space, newest first. At the top, the runs WAITING FOR YOU (parked for a
// yes; each opens its request). Then the one table, a page at a time in the
// server's order, with the find bar and the rail: Outcome, Plugin, Tool,
// Started by. The rail's picks filter ON THE SERVER (so Failed pages through
// every failure); its counts are those of the latest 200 runs. The search
// narrows the rows read so far. ?open=<run> opens a run (read on its own when
// the page has not brought it), ?request=<id> a waiting run, ?plugin=<id>
// starts the rail on one plugin.

const PAGE = 50;
const SAMPLE = 200;
const KIND_WORD = Object.fromEntries(STARTED_BY_KINDS.map((k) => [k.id, k.label]));
const capital = (s) => (s ? `${s[0].toUpperCase()}${s.slice(1)}` : s);

/** A run's tool by name: its plugin's lifecycle says it; else the run's own id, made readable. */
export function toolNameOf(run, plugins) {
    if (run.toolName) return run.toolName;
    // a plugin with no tools is its own one tool: no second name for it
    if (!run.toolId) return null;
    const p = run.pluginId ? plugins?.find?.(run.pluginId) : null;
    const t = (p?.tools || []).find((x) => String(x.tool_id ?? '') === String(run.toolId ?? ''));
    if (t?.name) return t.name;
    return run.toolId ? capital(String(run.toolId).replace(/_/g, ' ')) : null;
}

export default function RunsView() {
    const { groupId, section, page } = usePlace();
    const [params] = useSearchParams();
    const startPlugin = params.get('plugin');
    const [openId, open, close] = useOpen();
    const [requestId, openRequest, closeRequest] = useOpen('request');
    const plugins = usePluginList(groupId);
    const pluginsData = plugins.data;
    const waiting = usePluginRequests(groupId, { status: 'pending' });

    // THE RAIL, counted over the latest runs; its picks go to the server
    const sample = useRunFeed(groupId, { limit: SAMPLE });
    const sampleRuns = sample.data?.runs || [];
    const pluginNames = useMemo(() => {
        const m = {};
        sampleRuns.forEach((r) => { if (r.pluginId) m[r.pluginId] = r.name; });
        return m;
    }, [sampleRuns]);
    const tool = (r) => toolNameOf(r, pluginsData);
    const facets = useMemo(() => [
        { id: 'outcome', title: 'Outcome', of: (r) => r.outcome, order: [...OUTCOMES, 'running'], label: (k) => outcomeWord(k) },
        { id: 'plugin', title: 'Plugin', of: (r) => r.pluginId, label: (k) => pluginNames[k] || 'Plugin' },
        { id: 'tool', title: 'Tool', of: (r) => (r.toolId ? toolKey(r) : null), label: (k) => tool(sampleRuns.find((r) => toolKey(r) === k) || { toolId: toolOfKey(k) }) || 'Tool' },
        { id: 'startedBy', title: 'Started by', of: (r) => r.startedByKind, order: STARTED_BY_KINDS.map((k) => k.id), label: (k) => KIND_WORD[k] || capital(k) },
    ], [pluginNames, sampleRuns, pluginsData]); // eslint-disable-line react-hooks/exhaustive-deps
    const textOf = (r) => `${r.name} ${tool(r) || ''} ${r.startedBy || ''} ${r.realmName || ''} ${happenedLine(r) || ''}`.toLowerCase();
    const f = useFacets(sampleRuns, facets, {
        text: textOf, placeholder: 'Find a run', initial: startPlugin ? { plugin: [startPlugin] } : undefined,
    });
    // a run is one plugin's at a time: the Plugin facet picks one
    const rail = React.cloneElement(f.rail, { onToggle: (id, k, only) => f.rail.props.onToggle(id, k, id === 'plugin' ? true : only) });
    const picked = f.picked;
    const search = (f.rail.props.search || '').trim().toLowerCase();
    const toolKeys = picked.tool || [];
    const filters = {
        limit: PAGE,
        pluginId: (picked.plugin || [])[0] || null,
        outcome: picked.outcome?.length ? picked.outcome : null,
        startedBy: picked.startedBy?.length ? picked.startedBy : null,
        toolId: toolKeys.length ? [...new Set(toolKeys.map(toolOfKey))] : null,
    };
    const feed = useRunFeed(groupId, filters);
    const loaded = feed.data?.runs || [];
    const rows = loaded.filter((r) => (!toolKeys.length || toolKeys.includes(toolKey(r))) && (!search || textOf(r).includes(search)));

    const { entry, notice } = useLinkedRun(groupId, openId, feed);
    const reload = () => { feed.reload(); sample.reload(); waiting.reload(); };
    const pending = (waiting.data || []).filter((q) => q.status === 'pending');
    const filtering = f.filtering;
    const nothingYet = feed.data && !filtering && feed.data.total === 0;

    const waitingSet = pending.length > 0 && (
        <TitledSet title="Waiting for you" count={pending.length} className="plugins-runs__waiting">
            <div className="nx-rows plugins-runs__requests">
                {pending.map((q) => (
                    <Row
                        key={q.id}
                        open={String(q.id) === String(requestId)}
                        name={`${q.toolName || q.pluginName} · ${q.pluginName}`}
                        sub={`${q.askedBy} · ${q.why}`}
                        end={expiresWord(q.expires) || undefined}
                        endTone="warn"
                        onPress={() => openRequest(q.id)}
                    />
                ))}
            </div>
        </TitledSet>
    );

    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={feed.data ? feed.data.total : undefined}
            verbs={<Verb icon={<RefreshCw />} onClick={reload}>Refresh</Verb>}
            inspector={entry && (
                <RunInspector
                    key={entry.id} run={entry} tool={tool(entry)} onClose={close} onChanged={reload}
                    onOpenRun={(id) => open(id)} onOpenRequest={(id) => openRequest(id)}
                />
            )}
        >
            {notice && <Note tone={notice.tone}>{notice.text}</Note>}
            <Faceted id="plugins-runs" rail={rail} notice={waitingSet || null}>
                {feed.error && !feed.data && <ErrorEmpty what="the runs" error={feed.error} retry={feed.reload} />}
                {feed.data && !rows.length && (
                    <Empty>{nothingYet ? 'Nothing has run yet.' : 'No run matches.'}</Empty>
                )}
                {rows.length > 0 && (
                    <div className="plugins-runs">
                        <DataTable
                            id="plugins-runs" label="Runs" columns={runColumns(tool)} rows={rows}
                            keyOf={(r) => r.id} currentKey={openId} onOpen={open} fit
                        />
                    </div>
                )}
                {feed.data && !search && (
                    <ShowMore shown={loaded.length} total={feed.data.total} label={feed.moreLoading ? 'Reading…' : 'Older'} onMore={feed.more} />
                )}
                {feed.moreError && <Empty retry={feed.more}>The older runs couldn’t be read.</Empty>}
            </Faceted>
            {requestId && (
                <RequestSheet groupId={groupId} requestId={requestId} onClose={closeRequest} onAnswered={reload} />
            )}
        </NucleusPage>
    );
}

export const runColumns = (tool) => [
    {
        id: 'plugin', label: 'Plugin', lock: true, min: 150, grow: 1.2, className: 'plugins-table__name',
        cell: (r) => <span className="plugins-table__word">{r.name}</span>,
    },
    { id: 'tool', label: 'Tool', min: 110, grow: 0.9, keep: 2, cell: (r) => tool(r) || '—' },
    {
        id: 'outcome', label: 'Outcome', min: 96, grow: 0,
        cell: (r) => <span className="plugins-runs__outcome"><RunDot tone={dotTone(r.outcome)} />{outcomeWord(r.outcome)}</span>,
    },
    { id: 'startedBy', label: 'Started by', min: 120, grow: 1, keep: 1, cell: (r) => r.startedBy || '—' },
    { id: 'realm', label: 'Runs in', min: 100, grow: 0.7, keep: 3, cell: (r) => r.realmName || '—' },
    {
        id: 'happened', label: 'What happened', min: 160, grow: 2,
        cell: (r) => {
            const line = happenedLine(r);
            const bad = r.outcome === 'failed' || r.outcome === 'refused';
            return line ? <span className={bad ? 'is-live' : undefined}>{line}</span> : '—';
        },
    },
    { id: 'took', label: 'Took', num: true, min: 60, grow: 0, keep: 4, cell: (r) => tookWord(r.took) || '—' },
    { id: 'started', label: 'Started', min: 90, grow: 0, className: 'plugins-table__when', cell: (r) => ago(r.started, { never: 'never' }) },
];

// RUN AGAIN: an automation's run, through its automation (its grant, its
// settings); any other run of a plugin's tool, through /execute with the same
// tool, in the realm it ran in when this space still lets it (else the
// default). A tool that wants this run's values opens its sheet instead.
export async function runAgain(run, groupId) {
    if (run.automationId) {
        await autosApi.trigger(groupId, run.automationId);
        return { started: true };
    }
    const cfg = await pluginsApi.setupConfig(run.pluginId, groupId, run.toolId || null);
    const asks = (cfg?.fields || []).filter((f) => !f.secret && f.scope === 'runtime' && f.required && blank(f.value));
    if (asks.length) return { sheet: true };
    const realmId = startRealm(cfg?.run_realms || [], run.realmId);
    if (!realmId) throw new Error('No realm here lets it run.');
    try {
        const res = await pluginsApi.run(run.pluginId, groupId, { toolId: run.toolId || null, realmId, input: {} });
        return res?.consent_required ? { parked: true, requestId: res.request_id || null } : { execution: res?.execution || {} };
    } catch (e) {
        throw new Error(refusalOf(e));
    }
}

/** The realm a run again would run in, by name, for its confirm. */
function againRealm(run) {
    return run.realmName || 'its realm';
}

export function RunInspector({ run, tool, onClose, onChanged, onOpenRun, onOpenRequest }) {
    const { groupId, spaceName, to, navigate } = usePlace();
    const { runPlugins, pluginHint } = useSpaceAccess();
    const toast = useToast();
    const [reading, setReading] = useState(false);
    // THE NODES IT MADE, from the run read by its id (a run read that way brought them)
    const read = useRun(groupId, run.nodes ? null : run.id);
    const made = run.nodes ? { nodes: run.nodes, total: run.nodesTotal } : read.data ? { nodes: read.data.nodes, total: read.data.nodesTotal } : null;
    const word = outcomeWord(run.outcome);
    const toolHref = to('plugins', null, `open=${encodeURIComponent(run.pluginId)}&tool=${encodeURIComponent(run.toolId || '')}`);
    const someoneElse = ['app', 'agent'].includes(run.startedByKind);

    const actions = (run.automationId || run.pluginId) ? [{
        id: 'again', label: 'Run again', primary: true,
        disabled: !runPlugins, hint: runPlugins ? undefined : pluginHint,
        // an app's or agent's run, run again, is yours
        confirm: someoneElse ? { title: `Run ${tool || run.name} again?`, body: `Runs it as you, in ${againRealm(run)}.`, label: 'Run again', danger: false } : undefined,
        run: async () => {
            const out = await runAgain(run, groupId);
            if (out.sheet) { navigate(toolHref); return; }
            if (out.parked) {
                toast.info('Waiting for you.');
                if (out.requestId) onOpenRequest?.(out.requestId);
            } else if (out.execution) {
                const outcome = executionOutcome(out.execution);
                const said = `${tool || run.name}: ${outcomeWord(outcome)}.`;
                if (outcome === 'failed' || outcome === 'refused') toast.error(said); else toast.success(said);
                if (out.execution.run_id) onOpenRun?.(out.execution.run_id);
            } else {
                toast.success(`Running ${run.automation || run.name} again.`);
            }
            onChanged?.();
        },
    }] : [];

    const summary = run.summary || {};
    const c = runCounts(summary);
    const openNode = (id) => navigate(to('graph', null, `open=${encodeURIComponent(id)}`));
    const counted = [
        c.made > 0 && { label: 'Made', value: String(c.made) },
        c.changed > 0 && { label: 'Changed', value: String(c.changed) },
        c.removed > 0 && { label: 'Removed', value: String(c.removed) },
    ].filter(Boolean);
    const cannot = c.cannotWrite.length ? `Its realm doesn’t let it ${c.cannotWrite.join(', ')}.` : null;
    const did = counted.length > 0 || c.proposed > 0 || c.leftOut.length > 0 || cannot;

    return (
        <Inspector
            face={<PluginFace />}
            tag={`Run · ${word}`}
            title={run.name}
            sub={[tool, ago(run.started, { never: 'never' })].filter(Boolean).join(' · ')}
            actions={actions}
            onClose={onClose}
        >
            <Band title={spaceName}>
                <Facts items={[
                    { label: 'Outcome', value: word, tone: outcomeTone(run.outcome) === 'live' ? 'live' : outcomeTone(run.outcome) === 'warn' ? 'warn' : undefined },
                    { label: 'Started by', value: run.startedBy || '—' },
                    { label: 'Runs in', value: run.realmName || '—' },
                    { label: 'Tool', value: tool || '—' },
                    { label: 'Took', value: tookWord(run.took) || '—' },
                    { label: 'Started', value: whenOf(run.started) || '—' },
                ]} />
                {run.refused && <Note tone="live">{run.refused}</Note>}
                {!run.refused && run.error && <Note tone="live">{run.error}</Note>}
                {run.why && <Note>{run.why}</Note>}
                {c.hidden && <Note>Some details are only shown to admins.</Note>}
                {!runPlugins && pluginHint && <Note>{`${pluginHint}.`}</Note>}
            </Band>
            {did && (
                <Band title="What it did">
                    <Facts items={counted} />
                    {c.proposed > 0 && (
                        <Row name={`Proposed ${c.proposed}`} sub="Waiting in Review" onPress={() => navigate(to('graph', 'review'))} />
                    )}
                    {cannot && <Note tone="warn">{cannot}</Note>}
                    {c.leftOut.map((d) => <Row key={d.why} name={leftOutLine(d)} end={String(d.count)} endTone="warn" />)}
                </Band>
            )}
            {made && made.nodes.length > 0 && (
                <Band title="What it made" aside={made.total}>
                    {made.nodes.slice(0, 20).map((n) => (
                        <Row key={n.id} name={n.title || 'Untitled'} sub={n.schemaName || undefined} onPress={() => openNode(n.id)} />
                    ))}
                    {made.total > 20 && <Note>{`${made.total - 20} more.`}</Note>}
                </Band>
            )}
            <Band title="What ran">
                <Tags items={[
                    run.pluginId && { key: 'p', label: run.name, onPress: () => navigate(to('plugins', null, `open=${encodeURIComponent(run.pluginId)}`)) },
                    run.pluginId && tool && { key: 't', label: tool, onPress: () => navigate(toolHref) },
                    run.automationId && { key: 'a', label: run.automation && run.automation !== run.name && run.automation !== tool ? run.automation : 'Its automation', onPress: () => navigate(to('plugins', 'automations', `open=${encodeURIComponent(run.automationId)}`)) },
                ]} />
            </Band>
            <Band title="About" aside={summary.call_record ? <button type="button" className="nx-link" onClick={() => setReading(true)}>The call</button> : undefined}>
                <Facts items={[
                    { label: 'Finished', value: whenOf(run.finished) || '—' },
                    run.automation && { label: 'Automation', value: run.automation },
                ]} />
            </Band>
            {reading && <ReadSheet title={`${run.name} · ${whenOf(run.started)}`} parts={[{ label: 'The call', value: summary.call_record }]} onClose={() => setReading(false)} />}
        </Inspector>
    );
}

